import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, trackVisit } from "../lib/api";
import { ArrowRight, ShieldCheck, Sparkles, Coins, Gem, Star, Phone, MapPin } from "lucide-react";
import QuoteTable from "../components/site/QuoteTable";
import PreventivoForm from "../components/site/PreventivoForm";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/41158db5-39b3-4ac2-97f0-f1c825b2e80a/images/3a0d212e24c200d6feef519f495cd17363ab3e82cd294dc6fcf3b2fa9d01db5f.png";
const GOLD_BARS = "https://static.prod-images.emergentagent.com/jobs/41158db5-39b3-4ac2-97f0-f1c825b2e80a/images/3eeb812c0efe548fbe9accc3e9ca4fa029f23819e0c3017f43af4ef923c70009.png";
const JEWELRY = "https://static.prod-images.emergentagent.com/jobs/41158db5-39b3-4ac2-97f0-f1c825b2e80a/images/f5b1f79ef698005a14624a7ea31b15ca418d4422030e30d125b8d7e021ea7a96.png";

const iconMap = { Coins, Gem, Sparkles, ShieldCheck };

export default function Home() {
  const [content, setContent] = useState({});
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    trackVisit("home");
    api.get("/content").then((r) => setContent(r.data));
    api.get("/reviews").then((r) => setReviews(r.data.slice(0, 3)));
  }, []);

  const hero = content.hero || {};
  const services = content.services || [];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden grain">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/30 via-[#0a1628]/60 to-[#0a1628]" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pb-32 pt-40 w-full">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8 fade-in-up">
              <span className="inline-flex items-center gap-3 text-[0.7rem] uppercase tracking-[0.4em] text-[var(--gold)] mb-8">
                <span className="w-12 h-px bg-[var(--gold)]" />
                {hero.eyebrow || "Compro Oro Modena"}
              </span>
              <h1 className="font-serif text-5xl md:text-7xl lg:text-[5.5rem] leading-[1.05] text-white mb-8 tracking-tight">
                {hero.title_line1 || "Il valore del tuo oro,"}
                <br />
                <span className="gold-text italic">{hero.title_line2 || "trasformato in fiducia."}</span>
              </h1>
              <p className="text-base lg:text-lg text-[var(--text-secondary)] max-w-2xl leading-relaxed mb-10">
                {hero.subtitle}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contatti" data-testid="hero-cta-primary" className="btn-gold">
                  {hero.cta_primary || "Valutazione Gratuita"} <ArrowRight size={16} />
                </Link>
                <Link to="/quotazioni" data-testid="hero-cta-secondary" className="btn-ghost-gold">
                  {hero.cta_secondary || "Quotazioni Live"}
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4 fade-in-up-3 hidden lg:block">
              <div className="luxe-card p-7">
                <div className="text-[0.65rem] uppercase tracking-[0.3em] text-[var(--gold)] mb-3">Negozio</div>
                <div className="font-serif text-2xl text-white mb-4 leading-tight">Affari Oro<br />Modena</div>
                <div className="space-y-2 text-sm text-white/70">
                  <div className="flex gap-2"><MapPin size={14} className="text-[var(--gold)] mt-0.5 shrink-0" /><span>Via Fratelli Rosselli 265</span></div>
                  <div className="flex gap-2"><Phone size={14} className="text-[var(--gold)] mt-0.5 shrink-0" /><span>338 947 2101</span></div>
                </div>
                <div className="divider-gold my-5" />
                <div className="text-xs text-white/50">Lun-Sab 09:00-12:30 / 15:30-19:00</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAND */}
      <section className="py-16 border-y border-white/5 bg-[#060f1f]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-10">
          {[
            { n: "15+", l: "Anni di Esperienza" },
            { n: "5.000+", l: "Clienti Soddisfatti" },
            { n: "100%", l: "Trasparenza" },
            { n: "24h", l: "Pagamento Garantito" },
          ].map((s, i) => (
            <div key={i} className="text-center md:text-left">
              <div className="font-serif text-4xl md:text-5xl gold-text mb-2">{s.n}</div>
              <div className="text-[0.7rem] uppercase tracking-[0.25em] text-white/50">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* QUOTAZIONI DEL GIORNO */}
      <section className="py-24 bg-[#060f1f]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="text-[0.7rem] uppercase tracking-[0.4em] text-[var(--gold)]">— Quotazioni del giorno</span>
            <h2 className="font-serif text-5xl md:text-6xl text-white mt-4 leading-tight">
              Le nostre quotazioni <span className="gold-text italic">di oggi.</span>
            </h2>
            <p className="text-[var(--text-secondary)] mt-5 max-w-2xl mx-auto">
              Le quotazioni indicate sono <strong className="text-[var(--gold)]">prezzi di borsa</strong> del mercato internazionale (fonte: BullionVault). Per scoprire il <strong className="text-[var(--gold)]">reale valore</strong> del tuo oro o argento contattaci al <a href="tel:+393389472101" className="underline text-white">338 947 2101</a>.
            </p>
          </div>
          <QuoteTable />
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="py-28 bg-radial-spotlight">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-6 mb-16">
            <div className="lg:col-span-5">
              <span className="text-[0.7rem] uppercase tracking-[0.4em] text-[var(--gold)]">— Cosa acquistiamo</span>
              <h2 className="font-serif text-5xl md:text-6xl text-white mt-4 leading-tight">
                Solo oro <span className="gold-text italic">e argento.</span>
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-[var(--text-secondary)] text-base leading-relaxed">
                Specializzati esclusivamente nell'acquisto di oro e argento, di qualunque caratura e in qualsiasi forma. Valutazione professionale, trasparente, immediata.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s, i) => {
              const Icon = iconMap[s.icon] || Sparkles;
              return (
                <div key={i} data-testid={`service-card-${i}`} className="luxe-card p-8 group cursor-default">
                  <Icon size={28} className="text-[var(--gold)] mb-6" strokeWidth={1.2} />
                  <h3 className="font-serif text-2xl text-white mb-3">{s.title}</h3>
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PREVENTIVO ONLINE */}
      <PreventivoForm />

      {/* SPLIT IMAGE / TEXT */}
      <section className="py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img src={GOLD_BARS} alt="Lingotti d'oro" className="w-full h-[520px] object-cover rounded-sm" />
            <div className="absolute -bottom-8 -right-8 luxe-card p-6 max-w-xs hidden md:block">
              <div className="text-[0.65rem] uppercase tracking-[0.3em] text-[var(--gold)] mb-2">Carature</div>
              <div className="font-serif text-3xl text-white">24 · 22 · 21<br />18 · 14 · 9 kt</div>
              <div className="text-xs text-white/50 mt-2">+ Argento 925</div>
            </div>
          </div>
          <div>
            <span className="text-[0.7rem] uppercase tracking-[0.4em] text-[var(--gold)]">— Il processo</span>
            <h2 className="font-serif text-5xl text-white mt-4 mb-6 leading-tight">
              Quattro passi.<br/><span className="gold-text italic">Zero compromessi.</span>
            </h2>
            <div className="space-y-7 mt-10">
              {[
                ["01", "Valutazione gratuita", "Pesatura con bilance certificate e verifica della caratura con saggi non distruttivi."],
                ["02", "Quotazione trasparente", "Applichiamo la quotazione del giorno aggiornata sul nostro sito, senza spread nascosti."],
                ["03", "Conferma e contratto", "Documenti registrati secondo normativa italiana. Tracciabilità totale."],
                ["04", "Pagamento immediato", "Contanti entro i limiti di legge oppure bonifico per importi superiori."],
              ].map(([n, t, d]) => (
                <div key={n} className="flex gap-6 border-b border-white/5 pb-6">
                  <div className="font-serif gold-text text-3xl shrink-0">{n}</div>
                  <div>
                    <h4 className="font-serif text-xl text-white mb-1">{t}</h4>
                    <p className="text-sm text-[var(--text-secondary)]">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* REVIEWS PREVIEW */}
      <section className="py-28 bg-[#060f1f]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
            <div>
              <span className="text-[0.7rem] uppercase tracking-[0.4em] text-[var(--gold)]">— Recensioni</span>
              <h2 className="font-serif text-5xl text-white mt-4">La voce dei nostri clienti.</h2>
            </div>
            <Link to="/recensioni" className="btn-ghost-gold">Tutte le recensioni <ArrowRight size={14} /></Link>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {reviews.map((r) => (
              <div key={r.id} data-testid={`review-preview-${r.id}`} className="luxe-card p-8">
                <div className="flex gap-1 mb-5">
                  {[...Array(r.rating)].map((_, i) => (
                    <Star key={i} size={14} fill="#D4AF37" className="text-[var(--gold)]" />
                  ))}
                </div>
                <p className="font-serif text-lg text-white/90 leading-relaxed mb-6 italic">"{r.text}"</p>
                <div className="text-sm text-[var(--gold)] font-medium">{r.name}</div>
                <div className="text-xs text-white/40">{r.city}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={JEWELRY} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-[#0a1628]/80" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif text-5xl md:text-6xl text-white leading-tight mb-6">
            Pronto a scoprire il valore <span className="gold-text italic">del tuo oro?</span>
          </h2>
          <p className="text-[var(--text-secondary)] mb-10 text-lg">
            Una visita senza impegno, una valutazione professionale, una proposta onesta.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contatti" className="btn-gold">Prenota Valutazione <ArrowRight size={16} /></Link>
            <a href="tel:+393389472101" className="btn-ghost-gold"><Phone size={14} /> 338 947 2101</a>
          </div>
        </div>
      </section>
    </div>
  );
}
